import {
	DrmKeySystemAvailability,
	DrmRobustness,
	DrmStatusInterface,
	DrmType,
	HdcpStatus,
	HdcpStatusInterface,
	KeySystemStatusInterface,
	QualityCategory,
	ResourceConfigurationInterface
} from '@cbsinteractive/avia-js';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import SessionStore from 'library/ts/utilities/SessionStore';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AbstractResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/abstract/AbstractResourceConfigBuilder';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Base class for Resource Configuration builder classes for Desktop
 */
export abstract class AbstractDesktopResourceConfigBuilder extends AbstractResourceConfigBuilder {

	protected constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'AbstractDesktopResourceConfigBuilder',
				...argument
			} );
	}

	/**
	 * @description Returns filled resource configuration for playback on Desktop
	 * @returns {Promise<IPromiseResolveGeneric & {resource: IResourceConfig}>}
	 */
	getResult(): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }> {
		return new Promise(
			( resolve ) => {

				// create instance of resource config
				const resource: ResourceConfigurationInterface = window.avia.createResourceConfig();
				this?.logger?.log( 'getResult', 'created instance of ResourceConfigurationInterface using avia:', resource );

				// set values for desktop playback
				const promiseArray: { ( ...args ): Promise<any> }[] = [
					// IMPORTANT: must call getSiteSessionData to initialize siteSessionData before accessing it via getter: this.tag.model.siteUtilities.siteSessionData
					() => this.tag.model.siteUtilities.getSiteSessionData( this.name ), // load siteSessionData
					() => this.setAdaptiveBitrateSettings( resource ), // bitrate setting
					() => this.setContextMetadata( resource ), // set resource metadata for tracking
					() => this.setSyncbakSettings( resource ), // set playback settings for syncbak
					() => this.setOverrides( resource ) // set resource overrides
				];

				this.utils.Function.runPromiseArray( promiseArray ).then(
					() => {
						resolve( { msg: 'Resource config', resource: resource } );
					}
				).catch(
					( error ) => {
						// Resource config steps failed, but provide fallback with basic resource config
						this?.logger?.warn( 'Desktop resource config setup failed, using basic configuration:', error );
						resolve( { msg: 'Resource config (basic)', resource: resource } );
					}
				);
			}
		);
	}

	/**
	 * @description Limit screen resolution per HDCP detection results and drm type
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {ResourceConfigurationInterface}
	 */
	#hdcpBitrateProcessor = ( resource: ResourceConfigurationInterface ): Promise<ResourceConfigurationInterface> => {
		return new Promise(
			( resolve, _reject ) => {
				// get shorthand reference for DRM status
				// 🔄 ARCHITECTURAL BOUNDARY: Player Adapter → avia-js
				// Converts IPlayerAdapterDrmStatus to DrmStatusInterface for avia-js compatibility
				// Safe: identical underlying structure, just different type definitions
				const drmStatus = window?.SmartTag?.deviceCapabilities?.drm as unknown as DrmStatusInterface;

				/**
				 * @description Returns key system status object
				 * @param {DrmType} keySystem
				 * @returns {KeySystemStatusInterface}
				 */
				const getKeySystemStatus = ( keySystem: DrmType ): KeySystemStatusInterface => {
					const filteredKeySystemStatus = drmStatus.keySystemStatus.filter(
						( obj ) => {
							return (
								obj?.keySystem === keySystem
								&&
								obj?.availability === DrmKeySystemAvailability.DETECTED
							);
						}
					);

					return (
						(
							!this.utils.Type.isEmpty( filteredKeySystemStatus )
							&&
							this.utils.Type.isArray( filteredKeySystemStatus )
						)
							?
							filteredKeySystemStatus[ 0 ]
							:
							undefined
					);
				};

				/**
				 * @description Returns HDCP status object
				 * @param {number} version - minimum HDCP version required
				 * @returns {HdcpStatusInterface}
				 */
				const getHdcpStatus = ( version: number ): HdcpStatusInterface => {
					// pull hdcp version
					const hdcpStatus = drmStatus.hdcpStatus.filter(
						( obj ) => {
							return (
								parseFloat( obj?.version ) >= version
								&&
								obj?.status === HdcpStatus.USABLE
							);
						}
					);

					return (
						(
							!this.utils.Type.isEmpty( hdcpStatus )
							&&
							this.utils.Type.isArray( hdcpStatus )
						)
							?
							hdcpStatus[ 0 ]
							:
							undefined
					);
				};

				/**
				 * @description Sets maxCategory and returns updated resource
				 * @param {ResourceConfigurationInterface} resource
				 * @param {QualityCategory} maxCategory
				 * @returns {ResourceConfigurationInterface}
				 */
				const setMaxCategory = ( resource: ResourceConfigurationInterface, maxCategory: QualityCategory = QualityCategory.SD ): ResourceConfigurationInterface => {
					resource.playback = this.utils.DeepMerge.deepmerge(
						resource.playback,
						{
							abr: {
								maxCategory: maxCategory
							}
						}
					);
					// set max resolution to SD
					return resource;
				};

				// limit top bitrate to SD per HDCP detection results for widevine content
				// https://paramount.atlassian.net/browse/PLPSCORE-156 Early resolution cap for incompatible HDCP version
				if ( this.utils.Type.isEmpty( this?.params?.CONTENT?.drm?.widevine?.url ) === false ) {
					// get key system status for widevine
					const keySystemStatus = getKeySystemStatus( DrmType.WIDEVINE );
					// get hdcp status minimum version 2
					const hdcpStatus = getHdcpStatus( 2 );
					const hdcpVersion = parseFloat( hdcpStatus?.version );

					this?.logger?.log(
						'hdcpBitrateProcessor:',
						'keySystemStatus:',
						keySystemStatus,
						'hdcpStatus:',
						hdcpStatus,
						'hdcpVersion:',
						hdcpVersion
					);

					// CONDITIONS:
					// Bitrate limitations based off HDCP version and robustness
					// swSecureCrypto
					// swSecureDecode
					// undefined
					// Force 1024x576 - max SD resolution
					//
					// hwSecureCrypto + HDCP 2.0 or greater
					// hwSecureDecode + HDCP 2.0 or greater
					// hwSecureAll + HDCP 2.0 or greater
					// Can Play HD Content

					// filter by robustness
					switch ( keySystemStatus?.robustness ) {
						case DrmRobustness.SW_SECURE_CRYPTO:
						case DrmRobustness.SW_SECURE_DECODE:
						case DrmRobustness.UNKNOWN:
						case undefined: // HDCP detection produced no result
							resource = setMaxCategory( resource, QualityCategory.SD );
							break;
						// case DrmRobustness.HW_SECURE_CRYPTO:
						// case DrmRobustness.HW_SECURE_DECODE:
						// case DrmRobustness.HW_SECURE_ALL:
						//	if ( this.utils.Type.isNumber( hdcpVersion ) && hdcpVersion >= 2 ) {
						//		// all bitrates / resolutions should be available => no manifest processing is required
						//		this?.logger?.log(
						//			'hdcpBitrateProcessor:',
						//			`Robustness: ${keySystemStatus?.robustness} HDCP Version: ${hdcpVersion}`,
						//			'Do not limit bitrates.'
						//		);
						//	}
						//	break;
					}
				}

				this?.logger?.log( 'hdcpBitrateProcessor:', 'maxCategory:', resource?.playback?.abr?.maxCategory );

				resolve( resource );
			}
		);
	};

	/**
	 * @description Sets adaptive bitrate and disables capping bitrates on desktop-sized screens
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {Promise<ResourceConfigurationInterface>}
	 */
	protected setAdaptiveBitrateSettings( resource: ResourceConfigurationInterface ): Promise<ResourceConfigurationInterface> {
		return new Promise(
			( resolve, _reject ) => {
				// do not auto cap bitrates to screen size on desktop
				resource.playback.abr.capQualityToScreenSize = !this.capabilities.isDesktop;
				this?.logger?.log( 'setAdaptiveBitrateSettings', 'set resource.playback.abr.capQualityToScreenSize:', resource.playback.abr.capQualityToScreenSize );

				const promiseArray: { ( ...args ): Promise<any> }[] = [
					// IMPORTANT: must call getSiteSessionData to initialize siteSessionData before accessing it via getter: this.tag.model.siteUtilities.siteSessionData
					() => this.#hdcpBitrateProcessor( resource ), // hdcp resolution processing
					() => this.setStartBitrate( resource ) // force start bitrate to average low value between vod and live to speed up video start
				];

				this.utils.Function.runPromiseArray( promiseArray ).then(
					() => {
						resolve( resource );
					}
				);
			}
		);
	}

	/**
	 * @description add any custom dat from the session to the metadata
	 * @param resource
	 * @private
	 */
	#addCustomMetaDataToResource( resource: ResourceConfigurationInterface = {} ): ResourceConfigurationInterface {
		try {
			const sessionStore = SessionStore.getInstance();
			const data = sessionStore?.get( 'SFB_HB_data' );

			// Check if data exists
			if ( !data ) {
				return resource;
			}

			// Optional: Add basic validation
			if ( this.utils.Type.isObject( data ) ) {
				const modifiedMetaData = {
					...resource.metadata,
					...data
				};
				resource.metadata = modifiedMetaData;
				return resource;
			}

			return resource;

		} catch ( error ) {
			return resource;
		}
	}

	/**
	 * @description allows a reset of the custom metadata from session storage
	 * @private
	 */
	#resetCustomMetaData(): void {
		const sessionStore = SessionStore.getInstance();
		sessionStore?.remove( 'SFB_HB_data' );
	}

	/**
	 * @description Sets resource metadata for tracking
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {Promise<ResourceConfigurationInterface>}
	 */
	protected setContextMetadata( resource: ResourceConfigurationInterface ): Promise<ResourceConfigurationInterface> {
		/**
		 * @description Conviva specific device + framework parameters.
		 * @param {ResourceConfigurationInterface} resource
		 * @returns {ResourceConfigurationInterface}
		 */
		const setDeviceFramework = ( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface => {
			resource.metadata.deviceCategory = 'WEB';
			resource.metadata.frameworkName = 'SmartTag';
			resource.metadata.frameworkVersion = window?.PlayerServicesProjectInfo?.release_version;
			return resource;
		};

		return new Promise(
			( resolve, _reject ) => {
				// sanity check + instantiation of metadata object
				if ( this.utils.Type.isUndefined( resource?.metadata ) ) {
					resource.metadata = {};
				}

				// if incoming context metadata is a non-empty object
				if (
					!this.utils.Type.isEmpty( this.params?.CONTEXT?.metadata )
					&&
					this.utils.Type.isObject( this.params?.CONTEXT?.metadata )
				) {
					// add ALL context metadata properties to resource.metadata without parsing and processing
					// deep merge to prevent site from modifying metadata after assignment and to retain all values including 0, null, false
					// NOTE: We intentionally preserve 0, null, undefined values here. It is Tealium's responsibility to handle
					// these values appropriately on their end after metadata is passed to avia player and tracking services.
					resource.metadata = this.utils.DeepMerge.deepmerge(
						resource.metadata,
						this.params?.CONTEXT?.metadata
					);
				}

				// add any dynamic metadata from sessionStorage
				resource = this.#addCustomMetaDataToResource( resource );
				this.#resetCustomMetaData();

				// set device + framework
				resource = setDeviceFramework( resource );

				this?.logger?.log( 'setContextMetadata', 'resolving resource...', resource );
				// resolve promise returning altered resource config
				resolve( resource );
			}
		);
	}

	/**
	 * @description Sets appropriate settings for syncbak livestreams
	 * @param {ResourceConfigurationInterface} resource
	 * @returns {Promise<ResourceConfigurationInterface>}
	 */
	protected setSyncbakSettings( resource: ResourceConfigurationInterface ): Promise<ResourceConfigurationInterface> {
		return new Promise(
			( resolve, _reject ) => {
				const contentIsSyncbak = this.params?.CONTENT?.channelInfo?.streamType === 'syncbak';
				const settingsConfigEnabled = this?.tag?.model?.config?.tag?.Settings?.enabled;
				const syncbakPlayer = this?.tag?.model?.config?.tag?.Settings?.params?.playback?.syncbak?.player ?? 'hls';
				this?.logger?.log( 'setSyncbakSettings', 'content is syncbak:', contentIsSyncbak, 'Tag Settings configuration enabled:', settingsConfigEnabled, 'syncbak player:', syncbakPlayer );

				// if content is syncbak and configuration is set to use shaka for syncbak, then set adapter id to shaka in resource config
				if (
					contentIsSyncbak &&
					settingsConfigEnabled &&
					syncbakPlayer === 'shaka'
				) {
					this.setResourceAdapterSettings( resource, syncbakPlayer );
					this?.logger?.log( 'setSyncbakSettings', 'resolving modified resource...', resource );
					resolve( resource );
					return;
				}

				// settings are unset or set to hls (already the default for syncbak), no need to modify resource
				this?.logger?.log( 'setSyncbakSettings', 'resolving resource unmodified...', resource );
				resolve( resource );
			}
		);
	}
}